import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { VentureCategory } from "@/lib/stage-categories";

export interface StageMapping {
  id: string;
  client_id: string;
  ghl_stage_id: string;
  ghl_stage_name: string;
  ghl_pipeline_id: string;
  ghl_pipeline_name: string;
  venture_category: VentureCategory;
  sort_order: number;
}

export function useStageMapping(clientId: string | null | undefined) {
  const queryClient = useQueryClient();

  const { data: mappings = [], isLoading } = useQuery({
    queryKey: ['stage-mappings', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('stage_mappings')
        .select('*')
        .eq('client_id', clientId)
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as StageMapping[];
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const mapStage = useCallback((ghlStageId: string): VentureCategory => {
    const mapping = mappings.find(m => m.ghl_stage_id === ghlStageId);
    return mapping?.venture_category ?? 'unmapped';
  }, [mappings]);

  const getStageName = useCallback((ghlStageId: string): string => {
    const mapping = mappings.find(m => m.ghl_stage_id === ghlStageId);
    return mapping?.ghl_stage_name ?? ghlStageId;
  }, [mappings]);

  const isCategory = useCallback((ghlStageId: string, category: VentureCategory): boolean => {
    return mappings.find(m => m.ghl_stage_id === ghlStageId)?.venture_category === category;
  }, [mappings]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['stage-mappings', clientId] });
    queryClient.invalidateQueries({ queryKey: ['stage-mappings', 'all'] });
  }, [queryClient, clientId]);

  return { mappings, mapStage, getStageName, isCategory, isLoading, invalidate };
}

/**
 * Hook for admin views that need mappings for ALL clients.
 * Returns a Map<clientId, StageMapping[]> for batch lookup.
 */
export function useAllStageMappings() {
  const { data: allMappings = [], isLoading } = useQuery({
    queryKey: ['stage-mappings', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stage_mappings')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as StageMapping[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const byClient = useMemo(() => {
    const map = new Map<string, StageMapping[]>();
    for (const m of allMappings) {
      if (!map.has(m.client_id)) map.set(m.client_id, []);
      map.get(m.client_id)!.push(m);
    }
    return map;
  }, [allMappings]);

  const mapStageForClient = useCallback((clientId: string, ghlStageId: string): VentureCategory => {
    const clientMappings = byClient.get(clientId);
    if (!clientMappings) return 'unmapped';
    const mapping = clientMappings.find(m => m.ghl_stage_id === ghlStageId);
    return mapping?.venture_category ?? 'unmapped';
  }, [byClient]);

  const getStageNameForClient = useCallback((clientId: string, ghlStageId: string): string => {
    const clientMappings = byClient.get(clientId);
    if (!clientMappings) return ghlStageId;
    const mapping = clientMappings.find(m => m.ghl_stage_id === ghlStageId);
    return mapping?.ghl_stage_name ?? ghlStageId;
  }, [byClient]);

  return { allMappings, byClient, mapStageForClient, getStageNameForClient, isLoading };
}
